import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Honeypot, RgpdCheckbox } from "./ReservationForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full rounded-lg border border-terracotta/25 bg-white px-4 py-3 text-encre placeholder:text-taupe/60 focus:outline-none focus:ring-2 focus:ring-saffron/60";
const labelCls = "block text-sm font-semibold text-encre mb-1.5";

const OPTIONS = [
  { id: "service_table", label: "Service à table" },
  { id: "personnel", label: "Personnel de service" },
  { id: "location_vaisselle", label: "Location vaisselle" },
  { id: "location_mobilier", label: "Location mobilier" },
  { id: "livraison", label: "Livraison" },
  { id: "decoration", label: "Décoration" },
  { id: "animation", label: "Animation musicale" },
];

const SelectField = ({ id, testid, label, required = true, value, onChange, options, placeholder = "Sélectionner…" }) => (
  <div>
    <label htmlFor={id} className={labelCls}>{label}{required && " *"}</label>
    <select id={id} data-testid={testid} required={required} className={inputCls} value={value} onChange={onChange}>
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

export const DevisForm = () => {
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    type_demandeur: "", type_evenement: "", societe: "", nom: "", email: "", telephone: "",
    date_evenement: "", lieu: "", convives: 15, prestation: "", formule: "", options: [],
    contraintes: "", budget: "", description: "", source: "", prospection: false, rgpd: false, website: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const toggleOption = (id) => {
    setForm((f) => ({
      ...f,
      options: f.options.includes(id) ? f.options.filter((o) => o !== id) : [...f.options, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = {
        ...form,
        convives: Number(form.convives),
        societe: form.societe || null,
        formule: form.formule || null,
        contraintes: form.contraintes || null,
        budget: form.budget || null,
        description: form.description || null,
        source: form.source || null,
      };
      const { data } = await axios.post(`${API}/devis`, payload);
      toast.success(data.message);
      navigate("/merci?type=devis");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue. Veuillez réessayer ou nous appeler au 07 75 77 29 51.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="devis-form" id="devis">
      <Honeypot value={form.website} onChange={set("website")} />
      <div className="grid sm:grid-cols-2 gap-5">
        <SelectField id="dev-demandeur" testid="devis-type-demandeur" label="Vous êtes" value={form.type_demandeur} onChange={set("type_demandeur")}
          options={["Particulier", "Professionnel", "Association", "Autre"]} />
        <SelectField id="dev-evenement" testid="devis-type-evenement" label="Type d'événement" value={form.type_evenement} onChange={set("type_evenement")}
          options={["Mariage", "Fiançailles", "Anniversaire", "Baptême", "Événement d'entreprise", "Fête familiale", "Fête religieuse", "Autre"]} />
        <div>
          <label htmlFor="dev-societe" className={labelCls}>Société ou organisation (facultatif)</label>
          <input id="dev-societe" data-testid="devis-societe" className={inputCls} value={form.societe} onChange={set("societe")} />
        </div>
        <div>
          <label htmlFor="dev-nom" className={labelCls}>Nom et prénom *</label>
          <input id="dev-nom" data-testid="devis-nom" required className={inputCls} value={form.nom} onChange={set("nom")} autoComplete="name" />
        </div>
        <div>
          <label htmlFor="dev-email" className={labelCls}>Email *</label>
          <input id="dev-email" data-testid="devis-email" type="email" required className={inputCls} value={form.email} onChange={set("email")} autoComplete="email" />
        </div>
        <div>
          <label htmlFor="dev-tel" className={labelCls}>Téléphone *</label>
          <input id="dev-tel" data-testid="devis-telephone" type="tel" required className={inputCls} value={form.telephone} onChange={set("telephone")} autoComplete="tel" />
        </div>
        <div>
          <label htmlFor="dev-date" className={labelCls}>Date de l'événement *</label>
          <input id="dev-date" data-testid="devis-date" type="date" required className={inputCls} value={form.date_evenement} onChange={set("date_evenement")} />
        </div>
        <div>
          <label htmlFor="dev-convives" className={labelCls}>Nombre de convives * (min. 15)</label>
          <input id="dev-convives" data-testid="devis-convives" type="number" min="15" required className={inputCls} value={form.convives} onChange={set("convives")} />
        </div>
      </div>
      <div>
        <label htmlFor="dev-lieu" className={labelCls}>Lieu de l'événement (adresse complète) *</label>
        <input id="dev-lieu" data-testid="devis-lieu" required className={inputCls} value={form.lieu} onChange={set("lieu")} />
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <SelectField id="dev-prestation" testid="devis-prestation" label="Type de prestation souhaitée" value={form.prestation} onChange={set("prestation")}
          options={["Cocktail dînatoire", "Buffet froid", "Buffet chaud", "Repas assis", "Plateau repas", "Formule thé et pâtisseries", "Autre"]} />
        <div>
          <label htmlFor="dev-formule" className={labelCls}>Formule ou nombre de pièces par personne (facultatif)</label>
          <input id="dev-formule" data-testid="devis-formule" className={inputCls} value={form.formule} onChange={set("formule")} />
        </div>
      </div>
      <fieldset>
        <legend className={labelCls}>Options souhaitées</legend>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-1">
          {OPTIONS.map((o) => (
            <label key={o.id} className="flex items-center gap-2.5 text-sm text-taupe">
              <input
                type="checkbox"
                data-testid={`devis-option-${o.id}`}
                checked={form.options.includes(o.id)}
                onChange={() => toggleOption(o.id)}
                className="h-4 w-4 accent-saffron"
              />
              {o.label}
            </label>
          ))}
        </div>
      </fieldset>
      <div>
        <label htmlFor="dev-contraintes" className={labelCls}>Contraintes alimentaires (allergies, régimes, halal, végétarien…)</label>
        <input id="dev-contraintes" data-testid="devis-contraintes" className={inputCls} value={form.contraintes} onChange={set("contraintes")} />
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="dev-budget" className={labelCls}>Budget indicatif (facultatif)</label>
          <input id="dev-budget" data-testid="devis-budget" className={inputCls} value={form.budget} onChange={set("budget")} placeholder="Ex. : 3 000 €" />
        </div>
        <SelectField id="dev-source" testid="devis-source" label="Comment nous avez-vous connu ?" required={false} value={form.source} onChange={set("source")}
          options={["Bouche-à-oreille", "Recherche internet", "Réseaux sociaux", "Autre"]} />
      </div>
      <div>
        <label htmlFor="dev-description" className={labelCls}>Description du projet</label>
        <textarea id="dev-description" data-testid="devis-description" rows="4" className={inputCls} value={form.description} onChange={set("description")} />
      </div>
      <RgpdCheckbox checked={form.rgpd} onChange={set("rgpd")} idPrefix="devis" />
      <div className="flex items-start gap-3">
        <input id="dev-prospection" data-testid="devis-prospection" type="checkbox" checked={form.prospection} onChange={set("prospection")} className="mt-1 h-4 w-4 accent-saffron" />
        <label htmlFor="dev-prospection" className="text-sm text-taupe leading-relaxed">
          J'accepte de recevoir des offres et actualités de TN FOOD (facultatif).
        </label>
      </div>
      <button
        type="submit"
        data-testid="devis-submit"
        disabled={sending}
        className="rounded-full bg-saffron text-cream px-8 py-3.5 font-semibold transition-all duration-300 hover:bg-saffron-dark hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {sending ? "Envoi en cours…" : "Envoyer ma demande de devis"}
      </button>
    </form>
  );
};
